import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/http';
import { Notice, errorMessage, secondaryButton } from '../../components/common';
import { t, type MessageKey } from '../../i18n';

interface Item {
  id: string;
  title: string;
  instructions: string;
  subjectName: string;
  assignedOn: string;
  dueDate: string;
  status: 'pending' | 'submitted' | 'late' | 'excused' | 'missing';
  feedback: string | null;
  submittedOn: string | null;
  overdue: boolean;
}

/** A child's homework (for the student and for a parent): what is due, what is done, the teacher's feedback. */
export function ChildHomework({ studentId }: { studentId: string }) {
  const queryClient = useQueryClient();
  const key = ['me', 'children', studentId, 'homework'];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api<Item[]>(`/me/children/${studentId}/homework`),
  });
  const done = useMutation({
    mutationFn: (homeworkId: string) =>
      api<Item[]>(`/me/children/${studentId}/homework/${homeworkId}/done`, { method: 'POST' }),
    onSuccess: (data) => queryClient.setQueryData(key, data),
  });

  if (list.isPending) return <p role="status">{t('common.loading')}</p>;
  if (list.isError) return <Notice kind="error">{errorMessage(list.error)}</Notice>;
  if (list.data.length === 0) return <p className="text-slate-600">{t('hw.none')}</p>;
  const open = list.data.filter((h) => h.status === 'pending' || h.status === 'missing');
  return (
    <section aria-label={t('child.tab.homework')} className="space-y-3">
      <p className="text-sm text-slate-600">{t('hw.openCount', { n: open.length })}</p>
      {done.isError && <Notice kind="error">{errorMessage(done.error)}</Notice>}
      <ul className="space-y-3">
        {list.data.map((h) => (
          <li key={h.id} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-semibold">{h.title}</h3>
              <span
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  h.status === 'submitted' || h.status === 'late'
                    ? 'bg-green-100 text-green-800'
                    : h.overdue
                      ? 'bg-red-100 text-red-800'
                      : 'bg-slate-100 text-slate-700'
                }`}
              >
                {t(`hw.status.${h.status}` as MessageKey)}
                {h.overdue ? ` · ${t('hw.overdue')}` : ''}
              </span>
            </div>
            <p className="text-sm text-slate-600">
              {h.subjectName} · {t('hw.dueDate')}: {h.dueDate}
            </p>
            {h.instructions && <p className="mt-2 whitespace-pre-wrap text-sm">{h.instructions}</p>}
            {h.feedback && (
              <p className="mt-2 rounded bg-brand-50 p-2 text-sm">
                <span className="font-medium">{t('hw.feedback')}:</span> {h.feedback}
              </p>
            )}
            {(h.status === 'pending' || h.status === 'missing') && (
              <button
                type="button"
                className={`${secondaryButton} mt-3`}
                disabled={done.isPending}
                aria-label={`${t('hw.markDone')}: ${h.title}`}
                onClick={() => done.mutate(h.id)}
              >
                {t('hw.markDone')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
